import React, { useState } from 'react';
import {
  ShieldCheck,
  Upload,
  AlertTriangle,
  X,
  Calendar,
  FileCheck2,
  CheckCircle2,
  Eye,
  EyeOff,
} from 'lucide-react';
import { clinicaService } from '../services/clinica.service';
import type {
  MedicoCertificadoStatusResponse,
  MedicoPerfilParceiroContract,
} from '@sstexames/shared-types';

import FormField from './FormField';

interface CertificadoA1ModalProps {
  isOpen: boolean;
  onClose: () => void;
  medico: MedicoPerfilParceiroContract;
  onSuccess: (status: MedicoCertificadoStatusResponse) => void;
}

export default function CertificadoA1Modal({
  isOpen,
  onClose,
  medico,
  onSuccess,
}: CertificadoA1ModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [senha, setSenha] = useState('');
  const [showSenha, setShowSenha] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successInfo, setSuccessInfo] = useState<MedicoCertificadoStatusResponse | null>(null);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      if (
        !selected.name.toLowerCase().endsWith('.pfx') &&
        !selected.name.toLowerCase().endsWith('.p12')
      ) {
        setError('Por favor, selecione um arquivo válido de Certificado Digital (.pfx ou .p12).');
        setFile(null);
        return;
      }
      setFile(selected);
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Selecione o arquivo do certificado digital (.pfx ou .p12).');
      return;
    }
    if (!senha) {
      setError('Informe a senha do certificado digital.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Converter arquivo para Base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const res = reader.result as string;
          resolve(res);
        };
        reader.onerror = (err) => reject(err);
      });
      reader.readAsDataURL(file);
      const base64 = await base64Promise;

      const response = await clinicaService.uploadCertificado({
        pfx_base64: base64,
        senha,
      });

      setSuccessInfo(response);
      onSuccess(response);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
        (err as { message?: string })?.message ||
        'Erro ao validar certificado. Verifique o arquivo e a senha.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-100 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <ShieldCheck className="w-6 h-6 text-primary-200" />
            <h3 className="text-lg font-bold">Certificado Digital A1 do Médico</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-white/20 transition-colors text-white cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto space-y-5">
          {/* Status Atual */}
          <div className="p-4 rounded-xl border border-slate-200 bg-slate-50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Status Atual do Certificado
              </span>
              {medico.has_valid_certificate ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-primary-100 text-primary-800 border border-primary-300">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary-600" />
                  Válido e Ativo
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                  Obrigatório / Não Vinculado
                </span>
              )}
            </div>

            <div className="text-sm text-slate-700">
              <p>
                <strong>Médico:</strong> {medico.nome} (CRM {medico.crm}/{medico.uf})
              </p>
              {medico.certificado_validade && (
                <p className="flex items-center gap-1 mt-1 text-slate-600">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  Validade cadastrada:{' '}
                  {new Date(medico.certificado_validade).toLocaleDateString('pt-BR')}
                </p>
              )}
            </div>

            {!medico.has_valid_certificate && (
              <p className="text-xs text-amber-800 bg-amber-50 p-2.5 rounded-lg border border-amber-200">
                <strong>Obrigatório:</strong> O certificado digital ICP-Brasil A1 é indispensável para
                assinar digitalmente o ASO e realizar atendimentos pelo sistema.
              </p>
            )}
          </div>

          {/* Sucesso imediato */}
          {successInfo && (
            <div className="p-4 rounded-xl bg-primary-50 border border-primary-200 text-primary-900 space-y-2">
              <div className="flex items-center gap-2 font-bold text-primary-800">
                <CheckCircle2 className="w-5 h-5 text-primary-600" />
                Certificado Vinculado com Sucesso!
              </div>
              <p className="text-xs text-primary-700">{successInfo.message}</p>
              <div className="text-xs space-y-1 text-primary-800 pt-1">
                {successInfo.titular && <div><strong>Titular:</strong> {successInfo.titular}</div>}
                {successInfo.validade && (
                  <div>
                    <strong>Validade:</strong>{' '}
                    {new Date(successInfo.validade).toLocaleDateString('pt-BR')} (
                    {successInfo.dias_restantes} dias restantes)
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Formulário de Upload */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <FormField
              label="Arquivo do Certificado Digital A1 (.pfx ou .p12)"
              required
            >
              <div className="relative border-2 border-dashed border-slate-300 hover:border-primary-500 rounded-xl p-4 text-center cursor-pointer transition-colors bg-slate-50/50 hover:bg-primary-50/30">
                <input
                  type="file"
                  accept=".pfx,.p12,application/x-pkcs12"
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={loading}
                />
                <div className="flex flex-col items-center justify-center space-y-1.5">
                  <Upload className="w-8 h-8 text-primary-600" />
                  <span className="text-sm font-medium text-slate-700">
                    {file ? file.name : 'Clique para selecionar ou arraste o arquivo .pfx'}
                  </span>
                  <span className="text-xs text-slate-400">Padrão ICP-Brasil (Tipo A1)</span>
                </div>
              </div>
            </FormField>

            <FormField
              label="Senha do Certificado Digital"
              required
              helperText="A senha é armazenada com criptografia de ponta a ponta (AES-256)."
            >
              <div className="relative">
                <input
                  type={showSenha ? 'text' : 'password'}
                  required
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  placeholder="Digite a senha de proteção do arquivo"
                  className="w-full px-4 py-2.5 pr-10 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowSenha(!showSenha)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                >
                  {showSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </FormField>

            {error && (
              <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-xs flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
                <div>{error}</div>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-semibold text-primary-700 bg-white border border-primary-300 hover:bg-primary-50 hover:border-primary-400 rounded-xl transition-colors cursor-pointer"
                disabled={loading}
              >
                {successInfo ? 'Fechar' : 'Cancelar'}
              </button>
              <button
                type="submit"
                disabled={loading || !file || !senha}
                className="px-5 py-2.5 text-sm font-bold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl shadow-md transition-all flex items-center gap-2 cursor-pointer"
              >
                {loading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Validando Certificado...
                  </>
                ) : (
                  <>
                    <FileCheck2 className="w-4 h-4" />
                    Validar e Vincular Certificado
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
